import { Capacitor } from '@capacitor/core';
import { captureException } from '@sentry/react';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';

export interface StorageEngine {
  get: (key: string) => Promise<string | undefined>;
  set: (key: string, value: string) => Promise<void>;
  remove: (key: string) => Promise<void>;
}

/**
 * Preferred local storage engine that uses native secure storage.
 */
const secureStorageEngine: StorageEngine = {
  get: async (key: string): Promise<string | undefined> => {
    try {
      const item = await SecureStoragePlugin.get({ key });
      return item.value;
    } catch (error) {
      // The storage library does not use standard error objects, they instead
      // throw strings which really sucks.
      if (
        typeof error === 'string' &&
        error === 'Item with given key does not exist'
      ) {
        return undefined;
      }

      if (
        error instanceof Error &&
        error.message === 'Item with given key does not exist'
      ) {
        return undefined;
      }

      captureException(error, {
        level: 'warning',
        extra: {
          message: `Failed to get ${key} in KeyValueStore`,
        },
      });

      return undefined;
    }
  },

  set: async (key: string, value: string): Promise<void> => {
    try {
      await SecureStoragePlugin.set({ key, value });
    } catch (error) {
      captureException(error, {
        level: 'warning',
        extra: {
          message: `Failed to set ${key} in KeyValueStore`,
        },
      });
    }
  },

  remove: async (key: string): Promise<void> => {
    try {
      await SecureStoragePlugin.remove({ key });
    } catch (error) {
      captureException(error, {
        level: 'warning',
        extra: {
          message: `Failed to remove ${key} in KeyValueStore`,
        },
      });
    }
  },
};

const hasLocalStorageSupport = () => {
  const KEY = 'test-localstorage-availability';

  try {
    localStorage.setItem(KEY, 'value');
    localStorage.removeItem(KEY);

    return true;
  } catch (error) {
    console.warn('Browser localStorage is not available', error);
    return false;
  }
};

/**
 * Fallback storage engine that uses standard browser localStorage.
 * Only used if the secure storage plugin is not available.
 */
const localStorageEngine: StorageEngine = {
  async get(key: string): Promise<string | undefined> {
    const value = localStorage.getItem(key);

    if (value === null) {
      return undefined;
    }

    return value;
  },

  async set(key: string, value: string): Promise<void> {
    localStorage.setItem(key, value);
  },

  async remove(key: string): Promise<void> {
    localStorage.removeItem(key);
  },
};

const memoryStore: Record<string, string | undefined> = {};

/**
 * Fallback storage engine in case browser does not support
 * localStorage. This can occur due to multiple issues like
 * user denying it in browser, browser privacy features etc.
 */
const memoryStorageEngine: StorageEngine = {
  async get(key) {
    return memoryStore[key];
  },

  async set(key, value) {
    memoryStore[key] = value;
  },

  async remove(key: string) {
    memoryStore[key] = undefined;
  },
};

/**
 * This store is for less important data like caching small bits of
 * data. Depending on platform availability it might even be that
 * data is stored in memory thus vanishing if browser is e.g closed.
 */
export const VolatileKeyValueStore: StorageEngine = hasLocalStorageSupport()
  ? localStorageEngine
  : memoryStorageEngine;

export const KeyValueStore: StorageEngine =
  Capacitor.isPluginAvailable('SecureStoragePlugin') &&
  Capacitor.isNativePlatform()
    ? secureStorageEngine
    : VolatileKeyValueStore;
